/**
 * Sección "Autenticación 2FA" (TOTP).
 *
 * Tres estados:
 *   1. Sin MFA      → botón "Activar MFA" arranca el setup wizard.
 *   2. Setup wizard → muestra QR, espera código, activa.
 *   3. MFA activo   → status, regenerar backup codes, desactivar.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { ShieldCheck, ShieldOff, Copy, RotateCcw, Trash2 } from 'lucide-react'

import {
  ActionButton,
  DefList,
  DefRow,
  FieldRow,
  InlineAlert,
  SettingsSection,
  TextInput,
} from '@/components/ui/settings-primitives'
import {
  useBackupCodesStatus,
  useMfaDisable,
  useMfaSetup,
  useMfaVerify,
  useRegenerateBackupCodes,
} from '@/hooks/api/use-profile'
import type { ProfileResponse, TotpSetupResponse } from '@/lib/api'

export function MfaSection({ profile }: { profile: ProfileResponse }) {
  return profile.totp_enabled ? <MfaEnabled profile={profile} /> : <MfaDisabled />
}

// ─── State A: MFA off — show wizard CTA ────────────────────────────

function MfaDisabled() {
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)

  const setup = useMfaSetup()
  const verify = useMfaVerify()

  const onSetup = async () => {
    try {
      const data = await setup.mutateAsync()
      setSetupData(data)
    } catch {
      toast.error('No pudimos generar el secreto MFA')
    }
  }

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const resp = await verify.mutateAsync(code.trim())
      setBackupCodes(resp.backup_codes)
      setSetupData(null)
      setCode('')
      toast.success('MFA activado')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Código incorrecto')
    }
  }

  if (backupCodes) {
    return <BackupCodesDisplay codes={backupCodes} onClose={() => setBackupCodes(null)} />
  }

  if (setupData) {
    return (
      <SettingsSection
        title="Configurar autenticación 2FA"
        description="Escanea el QR con tu app de autenticación (Google Authenticator, Microsoft Authenticator, Authy, 1Password, Bitwarden, Aegis…)."
      >
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
          <div
            className="rounded-lg border border-border bg-white p-3 shadow-sm"
            dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
          />
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-fg-muted">
                Si no puedes escanear el QR
              </p>
              <p className="mt-1 break-all rounded-md border border-border bg-bg-app px-3 py-2 font-mono text-xs text-fg-primary">
                {setupData.secret_b32}
              </p>
            </div>
            <form onSubmit={onVerify} className="space-y-2">
              <FieldRow label="Código de 6 dígitos" hint="Ingresa el código que muestra tu app">
                <TextInput
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  required
                />
              </FieldRow>
              <ActionButton type="submit" loading={verify.isPending} disabled={code.length < 6}>
                Activar MFA
              </ActionButton>
            </form>
          </div>
        </div>
      </SettingsSection>
    )
  }

  return (
    <SettingsSection
      title="Autenticación de dos factores"
      description="Suma un segundo paso al iniciar sesión: además de tu contraseña, un código temporal de tu app de autenticación."
      footer={
        <ActionButton onClick={onSetup} loading={setup.isPending}>
          <ShieldCheck className="h-4 w-4" /> Activar MFA
        </ActionButton>
      }
    >
      <InlineAlert variant="warning">
        Tu cuenta está protegida solo por contraseña. Activa MFA para reducir
        drásticamente el riesgo de accesos no autorizados.
      </InlineAlert>
    </SettingsSection>
  )
}

// ─── State B: MFA enabled — manage ────────────────────────────────

function MfaEnabled({ profile: _profile }: { profile: ProfileResponse }) {
  const { data: bc } = useBackupCodesStatus()
  const [showRegen, setShowRegen] = useState(false)
  const [showDisable, setShowDisable] = useState(false)
  const [newCodes, setNewCodes] = useState<string[] | null>(null)

  const regen = useRegenerateBackupCodes()
  const disable = useMfaDisable()

  const onRegen = async (code: string) => {
    try {
      const resp = await regen.mutateAsync(code)
      setNewCodes(resp.backup_codes)
      setShowRegen(false)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos regenerar los códigos')
    }
  }

  const onDisable = async (password: string, code: string) => {
    try {
      await disable.mutateAsync({ password, code })
      toast.success('MFA desactivado')
      setShowDisable(false)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos desactivar MFA')
    }
  }

  if (newCodes) {
    return <BackupCodesDisplay codes={newCodes} onClose={() => setNewCodes(null)} />
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="MFA activo"
        description="Tu cuenta requiere un código TOTP además de tu contraseña."
      >
        <DefList>
          <DefRow term="Estado">
            <span className="inline-flex items-center gap-1.5 text-success-fg">
              <ShieldCheck className="h-4 w-4" />
              Activo
            </span>
          </DefRow>
          <DefRow term="Códigos de respaldo">
            {bc ? (
              <span>
                {bc.remaining}/{bc.total} disponibles
              </span>
            ) : (
              <span className="text-fg-muted">cargando…</span>
            )}
          </DefRow>
        </DefList>
        {bc && bc.remaining < 3 && (
          <div className="mt-3">
            <InlineAlert variant="warning">
              Te quedan pocos códigos de respaldo. Regenéralos para no perder acceso si pierdes
              tu app de autenticación.
            </InlineAlert>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Códigos de respaldo"
        description="Códigos de un solo uso que te permiten entrar si pierdes tu app de autenticación."
        footer={
          <ActionButton variant="secondary" onClick={() => setShowRegen((v) => !v)}>
            <RotateCcw className="h-4 w-4" />
            {showRegen ? 'Cancelar' : 'Regenerar códigos'}
          </ActionButton>
        }
      >
        {showRegen ? (
          <CodePromptForm
            label="Para regenerar necesitamos confirmar que tienes acceso a tu app."
            cta="Regenerar"
            loading={regen.isPending}
            onSubmit={(code) => onRegen(code)}
            fields={['code']}
          />
        ) : (
          <p className="text-sm text-fg-secondary">
            Si regeneras los códigos, los anteriores dejarán de funcionar. Guarda los nuevos
            en un lugar seguro.
          </p>
        )}
      </SettingsSection>

      <SettingsSection
        title="Desactivar MFA"
        description="Reducirás la seguridad de tu cuenta. Necesitarás contraseña + un código TOTP válido."
        footer={
          <ActionButton variant="danger" onClick={() => setShowDisable((v) => !v)}>
            <ShieldOff className="h-4 w-4" />
            {showDisable ? 'Cancelar' : 'Desactivar MFA'}
          </ActionButton>
        }
      >
        {showDisable && (
          <CodePromptForm
            label="Confirma con tu contraseña y un código TOTP."
            cta="Desactivar MFA"
            loading={disable.isPending}
            onSubmit={(code, password) => onDisable(password ?? '', code)}
            fields={['password', 'code']}
          />
        )}
      </SettingsSection>
    </div>
  )
}

// ─── Reusable code prompt form ─────────────────────────────────────

function CodePromptForm({
  label,
  cta,
  loading,
  onSubmit,
  fields,
}: {
  label: string
  cta: string
  loading: boolean
  onSubmit: (code: string, password?: string) => void
  fields: ('password' | 'code')[]
}) {
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(code.trim(), password)
      }}
      className="space-y-3"
    >
      <p className="text-sm text-fg-secondary">{label}</p>
      {fields.includes('password') && (
        <FieldRow label="Contraseña actual">
          <TextInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </FieldRow>
      )}
      {fields.includes('code') && (
        <FieldRow label="Código TOTP de 6 dígitos">
          <TextInput
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </FieldRow>
      )}
      <ActionButton
        type="submit"
        loading={loading}
        disabled={
          (fields.includes('code') && code.length < 6) ||
          (fields.includes('password') && !password)
        }
      >
        {cta}
      </ActionButton>
    </form>
  )
}

// ─── Backup codes display (one-time) ───────────────────────────────

function BackupCodesDisplay({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      toast.success('Códigos copiados')
    } catch {
      toast.error('No pudimos copiar al portapapeles')
    }
  }
  const download = () => {
    const blob = new Blob([codes.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'taxor-backup-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <SettingsSection
      title="Tus códigos de respaldo"
      description="Solo los verás esta vez. Guárdalos en un gestor de contraseñas o impreso en un lugar seguro."
      footer={
        <ActionButton onClick={onClose}>Ya los guardé</ActionButton>
      }
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {codes.map((c, i) => (
          <code
            key={i}
            className="rounded-md border border-border bg-bg-app px-2 py-1.5 text-center font-mono text-sm text-fg-primary"
          >
            {c}
          </code>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <ActionButton variant="secondary" onClick={copyAll}>
          <Copy className="h-4 w-4" />
          Copiar todos
        </ActionButton>
        <ActionButton variant="secondary" onClick={download}>
          <Trash2 className="h-4 w-4" />
          Descargar .txt
        </ActionButton>
      </div>
    </SettingsSection>
  )
}
